import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ApplicationStatusResponse } from '@codegen';
import { AgreementsRoutingConstants } from '@modules/agreements/constants/agreementsRouting.constants';
import { ConditionsRoutingConstants } from '@modules/conditions/constants/conditionsRouting.constants';
import {
  ConditionsTypes,
  GetProposalConditions,
  SendEvent,
  SendProposalCondition,
  SetDefaultCondition,
  SetProposalConditions,
} from '@modules/conditions/store/conditions.action';
import {
  ContractsRoutingConstants,
  ProposalRoutingConstants,
} from '@modules/order/order.const';
import { ProposalStatus } from '@modules/order/order.enum';
import { ScoringRoutingConstants } from '@modules/scoring/constants/scoringRouting.constants';
import { AppState } from '@modules/shared/store';
import {
  HideSpinnerLoading,
  IsHideSpinnerLoading,
} from '@modules/shared/store/common.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { concatMap, map, switchMap } from 'rxjs/operators';
import { SendProposalConditionData } from '../models/send-proposal-condition-data.interface';
import { DefaultService } from '@codegen/service';
import { StatusCacheService } from '@modules/shared/services/status-cache.service';
import { EMPTY } from 'rxjs';

@Injectable()
export class ConditionsEffects {
  private actions$ = inject(Actions);
  private router = inject(Router);
  private store = inject<Store<AppState>>(Store);
  private defaultService = inject(DefaultService);
  private statusCacheService = inject(StatusCacheService);

  public GetProposalConditions$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetProposalConditions>(ConditionsTypes.GetProposalConditions),
      map(data => data.payload),
      switchMap((proposalId: string) =>
        this.statusCacheService.getStatus(proposalId).pipe(
          switchMap((response: ApplicationStatusResponse) => {
            if (
              response.applicationStatus.key === ProposalStatus.offersPrepared
            ) {
              return this.defaultService.conditionsGet({
                applicationId: proposalId,
              });
            }

            this.router.navigateByUrl(
              `${'/order/' + ProposalRoutingConstants.proposalsList}`
            );

            return EMPTY;
          })
        )
      ),
      map(response => {
        if (response.defaultCondition) {
          this.store.dispatch(
            new SetDefaultCondition(response.defaultCondition)
          );
        } else {
          this.store.dispatch(new SetDefaultCondition(null));
        }

        this.store.dispatch(new SetProposalConditions(response.conditions));
      }),
      concatMap(() => [
        new HideSpinnerLoading(),
        new IsHideSpinnerLoading(true),
      ])
    )
  );

  public SendProposalCondition$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SendProposalCondition>(ConditionsTypes.SendProposalCondition),
      map(data => data.payload),
      switchMap((data: SendProposalConditionData) => {
        const { selectedConditionId, currentApplicationId } = data;

        return this.defaultService
          .conditionsSet({
            conditionId: selectedConditionId,
            applicationId: currentApplicationId,
          })
          .pipe(
            switchMap((res): Action[] => {
              switch (res.applicationStatus.key) {
                case ProposalStatus.partnerOfferReady:
                  this.router.navigateByUrl(
                    `/conditions/${ConditionsRoutingConstants.proposalOfferReady}/${currentApplicationId}`
                  );

                  return [];
                case ProposalStatus.processed:
                  if (res.contractId) {
                    this.router.navigateByUrl(
                      `/agreements/contract/${res.contractId}`
                    );
                  } else {
                    this.router.navigateByUrl(
                      `/order/${ContractsRoutingConstants.contractsList}`
                    );
                  }
                  return [];
                case ProposalStatus.agreementNeed:
                  this.router.navigateByUrl(
                    `/agreements/${AgreementsRoutingConstants.proposalAgreement}/${currentApplicationId}`
                  );
                  return [];
                case ProposalStatus.preScoringQuestionnaireNeed:
                  this.router.navigateByUrl(
                    `/scoring/${ScoringRoutingConstants.questionnaire}/${currentApplicationId}`
                  );
                  return [];
                case ProposalStatus.postScoringQuestionnaireNeed:
                  this.router.navigateByUrl(
                    `/scoring/${ScoringRoutingConstants.questionnaire}/${currentApplicationId}`
                  );
                  return [];
                default:
                  this.router.navigateByUrl(
                    `/order/${ProposalRoutingConstants.proposalsList}`
                  );
                  return [];
              }
            })
          );
      })
    )
  );

  public SendEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<SendEvent>(ConditionsTypes.SendEvent),
        map(data => data.payload),
        switchMap((data: string) =>
          this.defaultService.informationFinOrgsEvent({
            eventId: 'APPLICATION_OFFER_WHY_SUCH',
            params: {
              applicationId: data,
            },
          })
        )
      ),
    { dispatch: false }
  );
}
